import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Image as ImageIcon,
  Plus,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  UploadCloud,
  Pencil,
  Trash2,
  ArrowLeft,
  PackagePlus,
  Clock,
  Copy,
  GripVertical,
} from "lucide-react";
import { useDragReorder } from "@/lib/useDragReorder";
import { saveItemOrder } from "@/services/reorderService";
import { IMAGE_ACCEPT, isImageFile } from "@/lib/uploadImage";

const WEEKDAYS = [
  { key: "Mon", label: "Mon" },
  { key: "Tue", label: "Tue" },
  { key: "Wed", label: "Wed" },
  { key: "Thu", label: "Thu" },
  { key: "Fri", label: "Fri" },
  { key: "Sat", label: "Sat" },
  { key: "Sun", label: "Sun" },
];
const ALL_WEEKDAY_KEYS = WEEKDAYS.map((d) => d.key);

function stripUndefined(value) {
  if (Array.isArray(value)) return value.map(stripUndefined);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)]),
    );
  }
  return value;
}

/** Normalizes a possibly-legacy time entry (plain "HH:MM" string) into
 * { time, days }. Old Firestore docs saved before this feature just get
 * treated as "runs every day". */
function normalizeTimeSlot(entry) {
  if (typeof entry === "string") {
    return { time: entry, days: [...ALL_WEEKDAY_KEYS] };
  }
  return {
    time: entry.time,
    days: entry.days?.length ? entry.days : [...ALL_WEEKDAY_KEYS],
  };
}

function validateFerryForm(form) {
  const errors = {};

  const title = form.title.trim();
  if (!title) errors.title = "Ferry title is required.";
  else if (title.length < 3)
    errors.title = "Title must be at least 3 characters.";
  else if (title.length > 120)
    errors.title = "Title must be under 120 characters.";

  if (!form.destination.trim()) errors.destination = "Destination is required.";

  const originport = form.originport.trim();
  if (!originport) errors.originport = "Tagline is required.";
  else if (originport.length > 200)
    errors.originport = "Tagline must be under 200 characters.";

  const price = Number(form.price);
  if (form.price === "") errors.price = "Price is required.";
  else if (Number.isNaN(price)) errors.price = "Price must be a number.";
  else if (price <= 0) errors.price = "Price must be greater than 0.";
  else if (price > 200000)
    errors.price =
      "Price looks wrong — over SGD 200,000 per person. Double-check this.";

  if (!form.image.trim()) errors.image = "A hero image is required.";

  const emptyIncluded = form.included.some((i) => !i.trim());
  if (form.included.length === 0) {
    errors.included = "Add at least one thing that's included.";
  } else if (emptyIncluded) {
    errors.included = "Remove empty entries before saving.";
  }

  if (!form.departureTimes || form.departureTimes.length === 0) {
    errors.departureTimes = "Add at least one departure time.";
  }
  if (form.roundTrip && (!form.returnTimes || form.returnTimes.length === 0)) {
    errors.returnTimes =
      "Add at least one return time, or turn off Round Trip.";
  }

  return errors;
}

async function downloadImage(url, filename = "image.jpg") {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("Failed to fetch image");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed, opening image in a new tab instead:", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function filenameFromUrl(url, fallback) {
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const last = path.split("/").pop();
    return last || fallback;
  } catch {
    return fallback;
  }
}

const emptyForm = {
  title: "",
  description: "",
  destination: "",
  originport: "",
  price: "",
  type: "",
  image: "",
  roundTrip: true,
  departureTimes: [],
  returnTimes: [],
  included: [],
  amenities: [],
};

/** Maps a Firestore package doc back into the form shape. Numbers come back
 * as numbers from Firestore but the price input is a controlled string, so
 * it gets stringified here. departureTimes/returnTimes are normalized so
 * legacy plain-string entries still load correctly. */
function packageToForm(pkg) {
  return {
    title: pkg.title ?? "",
    description: pkg.description ?? "",
    destination: pkg.destination ?? "",
    originport: pkg.originport ?? "",
    price: pkg.price != null ? String(pkg.price) : "",
    type: pkg.type ?? "",
    image: pkg.image ?? "",
    roundTrip: Boolean(pkg.roundTrip),
    departureTimes: Array.isArray(pkg.departureTimes)
      ? pkg.departureTimes.map(normalizeTimeSlot)
      : [],
    returnTimes: Array.isArray(pkg.returnTimes)
      ? pkg.returnTimes.map(normalizeTimeSlot)
      : [],
    included: Array.isArray(pkg.included) ? pkg.included : [],
    amenities: Array.isArray(pkg.amenities) ? pkg.amenities : [],
  };
}

function formatTimeDisplay(value) {
  if (!value) return "";
  const [h, m] = value.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

function daysSummary(days) {
  if (!days || days.length === 0) return "No days selected";
  if (days.length === 7) return "Every day";
  return WEEKDAYS.filter((d) => days.includes(d.key))
    .map((d) => d.label)
    .join(", ");
}

/** Time-slot editor: add a time, then pick which weekdays it applies to via
 * a popup. Click an existing chip to re-open the popup and edit its days. */
function TimeSlotListEditor({ items, onChange }) {
  const [timeInput, setTimeInput] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [editingTime, setEditingTime] = useState(null); // null = adding new
  const [pendingTime, setPendingTime] = useState("");
  const [pendingDays, setPendingDays] = useState(ALL_WEEKDAY_KEYS);

  const sorted = [...items].sort((a, b) => a.time.localeCompare(b.time));

  const openAddPopup = () => {
    if (!timeInput) return;
    if (items.some((i) => i.time === timeInput)) return;
    setEditingTime(null);
    setPendingTime(timeInput);
    setPendingDays(ALL_WEEKDAY_KEYS);
    setPopupOpen(true);
  };

  const openEditPopup = (item) => {
    setEditingTime(item.time);
    setPendingTime(item.time);
    setPendingDays(item.days?.length ? item.days : ALL_WEEKDAY_KEYS);
    setPopupOpen(true);
  };

  const toggleDay = (key) => {
    setPendingDays((prev) =>
      prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key],
    );
  };

  const removeItem = (time) => onChange(items.filter((i) => i.time !== time));

  const confirmPopup = () => {
    if (pendingDays.length === 0) return;
    if (editingTime === null) {
      onChange([...items, { time: pendingTime, days: pendingDays }]);
      setTimeInput("");
    } else {
      onChange(
        items.map((i) =>
          i.time === editingTime ? { time: pendingTime, days: pendingDays } : i,
        ),
      );
    }
    setPopupOpen(false);
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input
          type="time"
          value={timeInput}
          onChange={(e) => setTimeInput(e.target.value)}
          className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
        />
        <button
          type="button"
          onClick={openAddPopup}
          disabled={!timeInput || items.some((i) => i.time === timeInput)}
          className="h-11 w-11 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {sorted.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sorted.map((item) => (
            <span
              key={item.time}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 pl-3 pr-1.5 py-1.5 text-sm text-gray-700"
            >
              <button
                type="button"
                onClick={() => openEditPopup(item)}
                className="text-left hover:text-[#0B2421] transition-colors"
                title="Click to edit which weekdays this applies to"
              >
                {formatTimeDisplay(item.time)}
                <span className="text-gray-400 text-xs ml-1">
                  · {daysSummary(item.days)}
                </span>
              </button>
              <button
                type="button"
                onClick={() => removeItem(item.time)}
                className="text-gray-400 hover:text-red-500 transition-colors ml-1"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <AnimatePresence>
        {popupOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setPopupOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-[#0B2421] mb-1">
                {formatTimeDisplay(pendingTime)}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Which days does this time run on?
              </p>

              <div className="grid grid-cols-4 gap-2 mb-2">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => toggleDay(d.key)}
                    className={`h-10 rounded-lg border text-xs font-semibold transition-colors ${
                      pendingDays.includes(d.key)
                        ? "bg-[#0B2421] text-white border-[#0B2421]"
                        : "bg-gray-50 text-gray-500 border-gray-200 hover:border-[#0B2421]"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setPendingDays(ALL_WEEKDAY_KEYS)}
                  className="text-xs font-semibold text-[#0B2421] hover:underline"
                >
                  Select all
                </button>
                <span className="text-gray-300">·</span>
                <button
                  type="button"
                  onClick={() => setPendingDays([])}
                  className="text-xs font-semibold text-[#0B2421] hover:underline"
                >
                  Clear
                </button>
              </div>

              {pendingDays.length === 0 && (
                <p className="text-xs text-red-600 mb-3">
                  Pick at least one day.
                </p>
              )}

              <div className="flex items-center gap-3 justify-between">
                {editingTime !== null ? (
                  <button
                    type="button"
                    onClick={() => {
                      removeItem(pendingTime);
                      setPopupOpen(false);
                    }}
                    className="text-sm font-semibold text-red-600 hover:underline"
                  >
                    Remove time
                  </button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPopupOpen(false)}
                    className="h-10 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmPopup}
                    disabled={pendingDays.length === 0}
                    className="h-10 px-4 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold disabled:opacity-40"
                  >
                    {editingTime !== null ? "Save" : "Add Time"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * @param {{
 *   onSubmit: (payload: any) => Promise<any>;                      // create
 *   onUpdate: (packageId: string, payload: any) => Promise<any>;   // edit
 *   onDelete: (packageId: string, imageUrl?: string) => Promise<any>;
 *   onFetchFerry: () => Promise<Array<any>>;
 *   onCancel?: () => void;
 *   onUploadImage: (file: File) => Promise<string>; // uploads to storage (GCS/Firebase), resolves to a public URL
 * }} props
 */
export default function CreateFerriesAdmin({
  onSubmit,
  onUpdate,
  onDelete,
  onFetchFerry,
  onCancel,
  onUploadImage,
}) {
  // Manage is the default landing view — see create-course.jsx for why.
  const [view, setView] = useState("manage"); // "form" | "manage"
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [newIncluded, setNewIncluded] = useState("");
  const [newAmenities, setNewAmenities] = useState("");

  const [packages, setFerry] = useState([]);
  const [loadingFerry, setLoadingFerry] = useState(false);
  const [packagesError, setFerryError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // package being confirmed for delete
  const [isDeleting, setIsDeleting] = useState(false);

  const [duplicatingId, setDuplicatingId] = useState(null);

  const fetchFerry = useCallback(async () => {
    setLoadingFerry(true);
    setFerryError(null);
    try {
      const list = await onFetchFerry();
      setFerry(list || []);
    } catch (err) {
      console.error("Failed to load packages:", err);
      setFerryError(err?.message || "Failed to load packages.");
    } finally {
      setLoadingFerry(false);
    }
  }, [onFetchFerry]);

  useEffect(() => {
    if (view === "manage") fetchFerry();
  }, [view, fetchFerry]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const addToList = (field, value, setValue) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setField(field, [...form[field], trimmed]);
    setValue("");
  };
  const removeFromList = (field, index) =>
    setField(
      field,
      form[field].filter((_, i) => i !== index),
    );

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
    setNewIncluded("");
    setNewAmenities("");
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const startCreate = () => {
    setEditingId(null);
    resetForm();
    setView("form");
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm(packageToForm(pkg));
    setErrors({});
    setTouched({});
    setSubmitError(null);
    setSubmitSuccess(false);
    setView("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const validationErrors = validateFerryForm(form);
    setErrors(validationErrors);
    setTouched(
      Object.keys(form).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

    if (Object.keys(validationErrors).length > 0) return;

    // Guard: Round Trip is off but return times are still populated —
    // the payload below discards them (returnTimes: form.roundTrip ? ... : [])
    // so confirm before silently wiping data the admin already entered.
    if (!form.roundTrip && form.returnTimes.length > 0) {
      const confirmDiscard = window.confirm(
        `Round Trip is set to "No", so the ${form.returnTimes.length} return time(s) you've added won't be saved. Continue anyway?`,
      );
      if (!confirmDiscard) return;
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      destination: form.destination.trim(),
      originport: form.originport.trim(),
      type: form.type.trim(),
      price: Number(form.price),
      image: form.image.trim(),
      included: form.included.map((i) => i.trim()).filter(Boolean),
      amenities: form.amenities.map((i) => i.trim()).filter(Boolean),
      roundTrip: form.roundTrip,
      departureTimes: form.departureTimes,
      returnTimes: form.roundTrip ? form.returnTimes : [],
    };

    setIsSubmitting(true);
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onSubmit(payload);
      }
      setSubmitSuccess(true);
      if (!editingId) resetForm();
    } catch (err) {
      console.error("Failed to save package:", err);
      setSubmitError(
        err?.message || "Failed to save package. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async (pkg) => {
    setFerryError(null);
    setDuplicatingId(pkg.id);
    try {
      const { id, createdAt, updatedAt, ...rest } = pkg;
      const payload = stripUndefined({
        ...rest,
        destination: `${pkg.destination} (Copy)`,
      });
      await onSubmit(payload);
    } catch (err) {
      console.error("Failed to duplicate Transport:", err);
      setFerryError(
        err?.message || "Failed to duplicate Transport. Please try again.",
      );
    } finally {
      setDuplicatingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await onDelete(deleteTarget.id, deleteTarget.image);
      setFerry((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete package:", err);
      setFerryError(err?.message || "Failed to delete package.");
    } finally {
      setIsDeleting(false);
    }
  };

  const fieldError = (field) =>
    touched[field] && errors[field] ? errors[field] : null;

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between w-full mb-2">
            <div className="flex items-center gap-3">
              {view === "form" && editingId && (
                <button
                  type="button"
                  onClick={() => setView("manage")}
                  className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <h1 className="text-3xl font-bold text-[#0B2421]">
                {view === "manage"
                  ? "Manage Ferry"
                  : editingId
                    ? "Edit Ferry"
                    : "Add a Ferry"}
              </h1>
            </div>

            {view === "form" ? (
              <button
                type="button"
                onClick={() => setView("manage")}
                className="h-12 px-6 rounded-xl border border-[#0B2421]/20 text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors"
              >
                Manage Ferry
              </button>
            ) : (
              <button
                type="button"
                onClick={startCreate}
                className="h-12 px-6 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors flex items-center gap-2"
              >
                <PackagePlus className="h-4 w-4" />
                New Ferry
              </button>
            )}
          </div>

          <p className="text-gray-500 mt-1">
            {view === "manage"
              ? "Edit or remove existing Ferries."
              : "Fields marked with an asterisk are required before this can go live."}
          </p>
        </motion.div>

        {view === "manage" ? (
          <ManageView
            packages={packages}
            loading={loadingFerry}
            error={packagesError}
            onEdit={startEdit}
            onDuplicate={handleDuplicate}
            duplicatingId={duplicatingId}
            onDeleteRequest={setDeleteTarget}
            deleteTarget={deleteTarget}
            onDeleteCancel={() => setDeleteTarget(null)}
            onDeleteConfirm={confirmDelete}
            isDeleting={isDeleting}
          />
        ) : (
          <>
            {submitSuccess && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
                {editingId
                  ? "Ferry updated successfully."
                  : "Ferry created successfully."}
              </div>
            )}
            {submitError && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <div className="lg:col-span-2 space-y-6">
                  <Section title="Hero Image" icon={ImageIcon}>
                    <FieldLabel required>Photo</FieldLabel>
                    <MainImageUpload
                      value={form.image}
                      onChange={(url) => setField("image", url)}
                      onBlur={() => markTouched("image")}
                      onUploadImage={onUploadImage}
                      error={fieldError("image")}
                    />
                  </Section>

                  <Section title="Basics">
                    <div className="space-y-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Field
                            label="Operator"
                            required
                            error={fieldError("title")}
                          >
                            <input
                              type="text"
                              value={form.title}
                              onChange={(e) =>
                                setField("title", e.target.value)
                              }
                              onBlur={() => markTouched("title")}
                              placeholder="e.g. Batam Fast Ferry"
                              className={inputClass(fieldError("title"))}
                            />
                          </Field>
                        </div>

                        <div className="flex-1">
                          <Field
                            label="Vessel Type"
                            error={fieldError("title")}
                          >
                            <input
                              type="text"
                              value={form.type}
                              onChange={(e) => setField("type", e.target.value)}
                              onBlur={() => markTouched("title")}
                              placeholder="High-Speed / Standard"
                              className={inputClass(fieldError("title"))}
                            />
                          </Field>
                        </div>
                      </div>

                      <Field
                        label="About This Service"
                        required
                        error={fieldError("description")}
                      >
                        <textarea
                          value={form.description}
                          onChange={(e) =>
                            setField("description", e.target.value)
                          }
                          onBlur={() => markTouched("description")}
                          rows={4}
                          placeholder="What makes this Ferry worth booking?"
                          className={`${inputClass(fieldError("description"))} h-auto py-3 resize-none`}
                        />
                        <div className="flex justify-end mt-1">
                          <span className="text-xs text-gray-400">
                            {form.description.length}/2000
                          </span>
                        </div>
                      </Field>

                      <Field
                        label="Origin Port"
                        required
                        icon={MapPin}
                        error={fieldError("destination")}
                      >
                        <input
                          type="text"
                          value={form.originport}
                          onChange={(e) =>
                            setField("originport", e.target.value)
                          }
                          onBlur={() => markTouched("originport")}
                          placeholder="e.g. HarbourFront Centre, Singapore"
                          className={inputClass(fieldError("destination"))}
                        />
                      </Field>

                      <Field
                        label="Destination Port"
                        required
                        icon={MapPin}
                        error={fieldError("destination")}
                      >
                        <input
                          type="text"
                          value={form.destination}
                          onChange={(e) =>
                            setField("destination", e.target.value)
                          }
                          onBlur={() => markTouched("destination")}
                          placeholder="e.g. Sekupang Ferry Terminal, Batam"
                          className={inputClass(fieldError("destination"))}
                        />
                      </Field>

                      <Field
                        label="Price per passenger"
                        required
                        error={fieldError("price")}
                      >
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-semibold">
                            SGD
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.price}
                            onChange={(e) => setField("price", e.target.value)}
                            onBlur={() => markTouched("price")}
                            placeholder="2500"
                            className={`${inputClass(fieldError("price"))} pl-16`}
                          />
                        </div>
                      </Field>
                    </div>
                  </Section>

                  <Section title="Included in Fee" icon={CheckCircle2}>
                    <ChipListEditor
                      items={form.included}
                      inputValue={newIncluded}
                      setInputValue={setNewIncluded}
                      onAdd={() => {
                        addToList("included", newIncluded, setNewIncluded);
                        markTouched("included");
                      }}
                      onRemove={(i) => removeFromList("included", i)}
                      placeholder="e.g., Port Tax"
                    />
                    {fieldError("included") && (
                      <FieldError message={errors.included} />
                    )}
                  </Section>

                  <Section title="Onboard Amenities" icon={CheckCircle2}>
                    <ChipListEditor
                      items={form.amenities}
                      inputValue={newAmenities}
                      setInputValue={setNewAmenities}
                      onAdd={() => {
                        addToList("amenities", newAmenities, setNewAmenities);
                        markTouched("amenities");
                      }}
                      onRemove={(i) => removeFromList("amenities", i)}
                      placeholder="e.g., Life Jackets"
                    />
                    {fieldError("included") && (
                      <FieldError message={errors.included} />
                    )}
                  </Section>

                  <Section title="Schedule" icon={Clock}>
                    <div className="space-y-5">
                      {/* <Field label="Round Trip Supported" required>
                        <div className="flex rounded-xl border border-gray-200 bg-gray-50 p-1 w-fit">
                          {[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                          ].map((opt) => (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() => setField("roundTrip", opt.value)}
                              className={`h-9 px-5 rounded-lg text-sm font-semibold transition-colors ${
                                form.roundTrip === opt.value
                                  ? "bg-[#0B2421] text-white"
                                  : "text-gray-500 hover:bg-gray-100"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <p className="mt-1.5 text-xs text-gray-400">
                          Controls whether Return Date / Return Time show on the
                          booking widget.
                        </p>
                      </Field> */}

                      <Field
                        label="Departure Time"
                        required
                        error={fieldError("departureTimes")}
                      >
                        <TimeSlotListEditor
                          items={form.departureTimes}
                          onChange={(list) => {
                            setField("departureTimes", list);
                            markTouched("departureTimes");
                          }}
                        />
                        <p className="mt-1.5 text-xs text-gray-400">
                          Add every outbound time this ferry runs, and pick
                          which weekdays each one applies to. Customers only see
                          times valid for the date they pick.
                        </p>
                      </Field>

                      {form.roundTrip && (
                        <Field
                          label="Return Time"
                          required
                          error={fieldError("returnTimes")}
                        >
                          <TimeSlotListEditor
                            items={form.returnTimes}
                            onChange={(list) => {
                              setField("returnTimes", list);
                              markTouched("returnTimes");
                            }}
                          />
                          <p className="mt-1.5 text-xs text-gray-400">
                            Same idea, for the return leg.
                          </p>
                        </Field>
                      )}
                    </div>
                  </Section>

                  <div className="flex items-center gap-3 justify-end pb-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="h-12 px-8 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {editingId ? "Saving..." : "Creating..."}
                        </>
                      ) : editingId ? (
                        "Save Changes"
                      ) : (
                        "Create Ferry"
                      )}
                    </button>
                  </div>
                </div>

                {/* Live preview */}
                <div className="hidden lg:block lg:sticky lg:top-8">
                  <LivePreview form={form} />
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Manage / list view ---------- */

function ManageView({
  packages,
  loading,
  error,
  onEdit,
  onDuplicate,
  duplicatingId,
  onDeleteRequest,
  deleteTarget,
  onDeleteCancel,
  onDeleteConfirm,
  isDeleting,
}) {
  const {
    items: orderedPackages,
    getDragHandlers,
    isDragging,
    isDragOver,
  } = useDragReorder(
    packages,
    (p) => p.id,
    (ids) => saveItemOrder("ferries", ids),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading Ferries...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
        <PackagePlus className="h-8 w-8 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No packages yet.</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5" />
        Drag a card to reorder — this is the order shown to golfers.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {orderedPackages.map((pkg) => (
          <div
            key={pkg.id}
            {...getDragHandlers(pkg)}
            className={`rounded-2xl border bg-white shadow-sm overflow-hidden group cursor-grab active:cursor-grabbing transition-opacity ${
              isDragging(pkg)
                ? "opacity-40 border-gray-100"
                : isDragOver(pkg)
                  ? "border-[#0B2421] ring-2 ring-[#0B2421]/30"
                  : "border-gray-100"
            }`}
          >
            <div className="relative h-36 bg-gray-100">
              <div className="absolute top-2 left-2 z-10 h-6 w-6 rounded-md bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80">
                <GripVertical className="h-3.5 w-3.5" />
              </div>
              {pkg.image ? (
                <img
                  src={pkg.image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => onEdit(pkg)}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>

                <button
                  type="button"
                  onClick={() => onDuplicate(pkg)}
                  disabled={duplicatingId === pkg.id}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40"
                >
                  {duplicatingId === pkg.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  Duplicate
                </button>

                <button
                  type="button"
                  onClick={() => onDeleteRequest(pkg)}
                  className="h-9 px-4 rounded-lg bg-white/90 text-red-600 text-sm font-semibold flex items-center gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
            <div className="p-4">
              {pkg.destination && (
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#D4AF37] mb-0.5">
                  {pkg.destination}
                </p>
              )}
              <h3 className="text-sm font-bold text-[#0B2421] truncate">
                {pkg.title}
              </h3>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 truncate pr-2">
                  {pkg.originport}
                </span>
                {pkg.price != null && (
                  <span className="text-sm font-bold text-[#0B2421] flex-shrink-0">
                    SGD {pkg.price}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={onDeleteCancel}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-[#0B2421] mb-2">
                Delete package?
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                This will permanently remove "{deleteTarget.title}" and its hero
                image. This can't be undone.
              </p>
              <div className="flex items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={onDeleteCancel}
                  disabled={isDeleting}
                  className="h-10 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDeleteConfirm}
                  disabled={isDeleting}
                  className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------- layout primitives (shared visual language with CreateCourseAdmin) ---------- */

function Section({ title, icon: Icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm"
    >
      <h2 className="text-lg font-bold mb-4 text-[#0B2421] flex items-center">
        {Icon && <Icon className="h-5 w-5 mr-2" />}
        {title}
      </h2>
      {children}
    </motion.div>
  );
}

function FieldLabel({ children, required, icon: Icon }) {
  return (
    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
      {Icon && <Icon className="inline h-4 w-4 mr-1" />}
      {children} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
  );
}

function Field({ label, required, icon, error, children }) {
  return (
    <div>
      <FieldLabel required={required} icon={icon}>
        {label}
      </FieldLabel>
      {children}
      {error && <FieldError message={error} />}
    </div>
  );
}

function inputClass(hasError) {
  return `w-full h-12 rounded-xl border px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 ${
    hasError ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
  }`;
}

function FieldError({ message }) {
  return (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />
      {message}
    </p>
  );
}

/* ---------- image upload (identical UX to CreateCourseAdmin) ---------- */

function MainImageUpload({ value, onChange, onBlur, onUploadImage, error }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (!file || !isImageFile(file)) {
        setLocalError("Please choose an image file.");
        return;
      }
      setLocalError(null);
      setIsUploading(true);
      try {
        const url = await onUploadImage(file);
        onChange(url);
      } catch (err) {
        console.error("Image upload failed:", err);
        setLocalError(err?.message || "Upload failed. Try again.");
      } finally {
        setIsUploading(false);
        onBlur?.();
      }
    },
    [onUploadImage, onChange, onBlur],
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (value && !isUploading) {
    return (
      <div className="relative group rounded-xl overflow-hidden border border-gray-200 h-56">
        <img
          src={value}
          alt="Ferry hero preview"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={() =>
              downloadImage(value, filenameFromUrl(value, "main-photo.jpg"))
            }
            className="px-4 py-2 rounded-lg bg-white/90 text-green-600 text-sm font-semibold"
          >
            Download
          </button>

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="px-4 py-2 rounded-lg bg-white text-[#0B2421] text-sm font-semibold"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            className="px-4 py-2 rounded-lg bg-white/90 text-red-600 text-sm font-semibold"
          >
            Remove
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center h-56 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
          isDragging
            ? "border-[#0B2421] bg-[#0B2421]/5"
            : error || localError
              ? "border-red-300 bg-red-50"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-6 w-6 text-[#0B2421] animate-spin mb-2" />
            <span className="text-sm text-gray-500">Uploading...</span>
          </>
        ) : (
          <>
            <UploadCloud className="h-6 w-6 text-gray-400 mb-2" />
            <span className="text-sm font-medium text-gray-600">
              Click to upload or drag a photo here
            </span>
            <span className="text-xs text-gray-400 mt-1">
              This shows as the hero banner on the package page
            </span>
          </>
        )}
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
      {(error || localError) && <FieldError message={error || localError} />}
    </div>
  );
}

/* ---------- chip list (identical to CreateCourseAdmin) ---------- */

function ChipListEditor({
  items,
  inputValue,
  setInputValue,
  onAdd,
  onRemove,
  placeholder,
}) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
        />
        <button
          type="button"
          onClick={onAdd}
          className="h-11 w-11 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white flex items-center justify-center flex-shrink-0 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-700"
            >
              {item}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- live preview — mirrors the real FerryDetail hero + booking card ---------- */

function LivePreview({ form }) {
  const hasContent = form.title || form.image || form.destination;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-[#0B2421]">
        <span className="text-xs font-semibold tracking-wide text-[#D4AF37] uppercase">
          Preview
        </span>
      </div>

      {!hasContent ? (
        <div className="p-8 text-center text-sm text-gray-400">
          Fill in the form to see how this package will appear to golfers.
        </div>
      ) : (
        <div>
          <div className="relative h-40 bg-gray-100">
            {form.image ? (
              <img
                src={form.image}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              {form.destination && (
                <p className="text-[#D4AF37] text-[10px] font-semibold uppercase tracking-widest mb-0.5">
                  {form.destination}
                </p>
              )}
              <h3 className="text-white font-bold text-base leading-snug">
                {form.title || "Ferry title"}
              </h3>
            </div>
          </div>

          <div className="p-5">
            {form.originport && (
              <p className="text-sm text-gray-500 mb-3">{form.originport}</p>
            )}

            {form.price && (
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-[#0B2421] font-bold text-2xl">
                  SGD {form.price}
                </span>
                <span className="text-gray-400 text-sm">Per Person</span>
              </div>
            )}

            {form.included.length > 0 && (
              <div className="space-y-1.5">
                {form.included.slice(0, 4).map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-xs text-gray-600">{item}</span>
                  </div>
                ))}
                {form.included.length > 4 && (
                  <span className="text-xs text-gray-400 pl-5">
                    +{form.included.length - 4} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
